// SearchResultItem — one search result row, shared by the Cmd-K palette and the
// `/search` page (RFC-0011 Task 1.5; RFC-0007 DTO). Purely presentational and
// controlled: it renders a single `SearchHit` and calls `onSelect` when clicked.
// It owns NO routing and NO listbox/option ARIA — the parent surface supplies
// `role`/keyboard nav/`aria-activedescendant` and builds the navigation target
// from the hit's `repo`/`path`.
//
// XSS posture (PD-0017 P5): the untrusted `snippet` is split by
// `parseHighlightSegments` and rendered as text nodes + `<mark>` elements —
// NEVER via `dangerouslySetInnerHTML`. The `title` is always plain text
// (RFC-0007 never highlights titles).

import { Fragment } from 'react';
import type { SearchHit } from '@portal/shared-types';
import { parseHighlightSegments } from '@/lib/highlight';
import { DocBadges } from '@/components/doc-reader/doc-badges';
import { Badge } from '@/components/ui/badge';

interface SearchResultItemProps {
  result: SearchHit;
  /** Fired on click; the parent owns navigation (builds `/projects/:repo/docs/<path>`). */
  onSelect: (result: SearchHit) => void;
  /** Highlighted state for the parent's keyboard model (drives styling only). */
  isActive?: boolean;
  /** DOM id so the parent can wire `aria-activedescendant` to this row. */
  id?: string;
}

export function SearchResultItem({ result, onSelect, isActive = false, id }: SearchResultItemProps) {
  const owner = result.docOwner?.trim() || null;
  const segments = parseHighlightSegments(result.snippet);

  return (
    <div
      id={id}
      onClick={() => onSelect(result)}
      className={[
        'cursor-pointer rounded-lg px-3 py-2 transition-colors',
        isActive ? 'bg-accent-faint' : 'hover:bg-accent-faint',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Title — always plain text. */}
          <div className="truncate text-sm font-medium text-text">{result.title}</div>
          {/* Repo breadcrumb. */}
          <div className="truncate text-xs text-muted">
            {result.repo} / {result.path}
          </div>
        </div>
        {(result.docType || result.docStatus || owner) && (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            <DocBadges docType={result.docType} docStatus={result.docStatus} />
            {owner && <Badge variant="ghost">{owner}</Badge>}
          </div>
        )}
      </div>

      {/* Snippet — highlighted segments as text nodes + <mark>, never HTML. */}
      {segments.length > 0 && (
        <p className="mt-1 line-clamp-2 text-xs text-text-2">
          {segments.map((seg, i) =>
            seg.marked ? (
              <mark
                key={i}
                className="rounded-[3px] bg-warning-bg px-0.5 text-badge-warning-text"
              >
                {seg.text}
              </mark>
            ) : (
              <Fragment key={i}>{seg.text}</Fragment>
            ),
          )}
        </p>
      )}
    </div>
  );
}
